import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowLeft, ArrowRight } from "../shell/approved-icons.js";
import { Button } from "./ui/button.js";
import "../styles/card-gallery.css";

/** Native touch scrolling, visible arrows and position; never auto-advances. */
export function CardGallery(props: {
  label: string;
  children: ReactNode[];
  more?: (() => void) | undefined;
  loading?: boolean | undefined;
}) {
  const track = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);

  const count = props.children.length;
  const visible = useGalleryColumns(track, count);
  const move = (next: number) =>
    track.current?.children[next]?.scrollIntoView({
      behavior: "auto",
      block: "nearest",
      inline: "start",
    });
  const onScroll = () => {
    const node = track.current;
    if (!node?.firstElementChild) return;
    const width = node.firstElementChild.getBoundingClientRect().width + 12;
    setIndex(
      Math.min(count - 1, Math.max(0, Math.round(node.scrollLeft / width))),
    );
  };
  return (
    <div
      className="fr-card-gallery"
      data-single={count === 1}
      data-dense={count > 2}
    >
      <div
        className="fr-card-track"
        ref={track}
        onScroll={onScroll}
        role="region"
        aria-label={props.label}
      >
        {props.children.map((child, i) => (
          <div
            className="fr-card-slide"
            key={i}
            role="group"
            aria-label={`${i + 1} of ${count}`}
          >
            {child}
          </div>
        ))}
      </div>
      <GalleryControls
        count={count}
        index={index}
        visible={visible}
        label={props.label}
        more={Boolean(props.more)}
        move={move}
      />
      {props.more && (
        <Button
          variant="ghost"
          size="block"
          disabled={props.loading}
          onClick={props.more}
        >
          {props.loading ? "Loading…" : "Load earlier rounds"}
        </Button>
      )}
    </div>
  );
}

function GalleryControls(props: {
  count: number;
  index: number;
  visible: number;
  label: string;
  more: boolean;
  move(index: number): void;
}) {
  const { count, index, move, visible } = props;
  return (
    <>
      {(count > 1 || props.more) && (
        <div className="fr-gallery-controls">
          <span aria-live="polite">
            {Math.min(index + 1, count)}
            {visible > 1 ? `–${Math.min(index + visible, count)}` : ""} /{" "}
            {count}
            {props.more ? "+" : ""}
          </span>
          <div>
            <Button
              variant="ghost"
              className="fr-gallery-arrow"
              aria-label={`Previous ${props.label}`}
              disabled={index === 0}
              onClick={() => move(Math.max(0, index - visible))}
            >
              <ArrowLeft />
            </Button>
            <Button
              variant="ghost"
              className="fr-gallery-arrow"
              aria-label={`Next ${props.label}`}
              disabled={index + visible >= count}
              onClick={() => move(Math.min(count - visible, index + visible))}
            >
              <ArrowRight />
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

function useGalleryColumns(
  track: React.RefObject<HTMLDivElement | null>,
  count: number,
) {
  const [visible, setVisible] = useState(1);
  useEffect(() => {
    const node = track.current;
    if (!node) return;
    const update = () => {
      const width =
        node.firstElementChild?.getBoundingClientRect().width ??
        node.clientWidth;
      setVisible(
        Math.max(1, Math.floor((node.clientWidth + 12) / (width + 12))),
      );
    };
    const observer = new ResizeObserver(update);
    observer.observe(node);
    update();
    return () => observer.disconnect();
  }, [count]);
  return visible;
}
